import { Box, Stack } from '@mui/material'
import { FormTextInputDisplay } from '../form-field-components/new-and-edit/form-text-input'
import { FormSelectInputDisplay } from '../form-field-components/new-and-edit/form-select-input'
import CategoryService from '../../../shared/services/category/category.service';
import { ICategory } from '../../../shared/services/category/category.model';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DialogSaveButtons } from '../miscellaneous-components/dialog-save-buttons';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import MeasurementUnitService from '../../../shared/services/measurement-unit/measurement-unit.service';
import { IMeasurementUnit } from '../../../shared/services/transaction/transaction.model';
import GreenHouseGasUnitsService from '../../../shared/services/green-house-gas-units/green-house-gas-units.service';
import { IGreenHouseGasUnit } from '../../../shared/services/green-house-gas-units/green-house-gas-units.model';
import { IPostConversionFactorCustom } from '../../../shared/services/conversion-factor-custom/conversion-factor-custom.model';
import { FormCheckBoxInputDisplay } from '../form-field-components/new-and-edit/form-checkbox-input';

export const SuperAdminAddConversionForm: FunctionComponent<{
  isEditPage?: boolean,
  editField?: string,
  setEditField?: (field: string) => void,
  newConversion: IPostConversionFactorCustom,
  submitHandler: (values: IPostConversionFactorCustom) => void,
  isSubmitting: boolean,
  conversionSourceId: string,
}> = ({
  isEditPage = false,
  editField,
  setEditField,
  newConversion,
  submitHandler,
  isSubmitting,
  conversionSourceId,
}) => {
    const navigate = useNavigate();
    //Services
    const categoriesService = new CategoryService();
    const measurementUnitsService = new MeasurementUnitService();
    const greenHouseGasUnitsService = new GreenHouseGasUnitsService();
    //State
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [measurementUnits, setMeasurementUnits] = useState<IMeasurementUnit[]>([]);
    const [greenHouseGasUnits, setGreenHouseGasUnits] = useState<IGreenHouseGasUnit[]>([]);
    //Use Effects
    useEffect(() => {
      categoriesService.getAllCategories()
        .then(response => {
          setCategories(response)
        })
        .catch(err => console.log(err))

      measurementUnitsService.getAll()
        .then(response => {
          setMeasurementUnits(response)
        })
        .catch(err => console.log(err))

      greenHouseGasUnitsService.getAll()
        .then(response => {
          setGreenHouseGasUnits(response)
        })
        .catch(err => console.log(err))
    }, []);

    useEffect(() => {
      formik.setValues({
        detailLevelOne: newConversion.detailLevelOne,
        detailLevelTwo: newConversion.detailLevelTwo,
        detailLevelThree: newConversion.detailLevelThree,
        measurementUnitId: newConversion.measurementUnitId,
        greenHouseGasUnitId: newConversion.greenHouseGasUnitId,
        multiplier: newConversion.multiplier,
        categoryId: newConversion.categoryId,
        conversionSourceId: newConversion.conversionSourceId,
        scope: newConversion.scope,
        isRenewable: newConversion.isRenewable,
      })
    }, [newConversion])

    //Formik
    const conversionSchema = Yup.object().shape({
      detailLevelOne: Yup.string().required('You must provide a detail level one'),
      detailLevelTwo: Yup.string().nullable(),
      detailLevelThree: Yup.string().nullable(),
      measurementUnitId: Yup.string().required('You must provide a measurement unit'),
      greenHouseGasUnitId: Yup.string().required('You must provide a greenhouse gas unit'),
      multiplier: Yup.number().required('You must provide a multiplier'),
      categoryId: Yup.string().required('You must provide a category'),
      conversionSourceId: Yup.string().required('You must provide a conversion source'),
      scope: Yup.string().required('You must provide a scope'),
      isRenewable: Yup.boolean().required('You must provide a renewable status'),
    })

    const formik = useFormik<IPostConversionFactorCustom>({
      initialValues: {
        detailLevelOne: newConversion.detailLevelOne,
        detailLevelTwo: newConversion.detailLevelTwo,
        detailLevelThree: newConversion.detailLevelThree,
        measurementUnitId: newConversion.measurementUnitId,
        greenHouseGasUnitId: newConversion.greenHouseGasUnitId,
        multiplier: newConversion.multiplier,
        categoryId: newConversion.categoryId,
        conversionSourceId: newConversion.conversionSourceId,
        scope: newConversion.scope,
        isRenewable: newConversion.isRenewable,
      },
      validationSchema: conversionSchema,
      onSubmit: (values) => {
        submitHandler(values)
      }
    })

    //Formik Event Handlers
    const cancelEditHandler = () => {
      if (setEditField) {
        setEditField('')
      }
      if (newConversion) {
        formik.setValues({
          detailLevelOne: newConversion.detailLevelOne,
          detailLevelTwo: newConversion.detailLevelTwo,
          detailLevelThree: newConversion.detailLevelThree,
          measurementUnitId: newConversion.measurementUnitId,
          greenHouseGasUnitId: newConversion.greenHouseGasUnitId,
          multiplier: newConversion.multiplier,
          categoryId: newConversion.categoryId,
          conversionSourceId: newConversion.conversionSourceId,
          scope: newConversion.scope,
          isRenewable: newConversion.isRenewable,
        })
      }
    }

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormSelectInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          allowEdit={true}
          disabled={isEditPage && editField !== 'scope'}
          title='Scope'
          name='scope'
          isTopRow={true}
          value={formik.values.scope}
          touched={formik.touched.scope}
          errors={formik.errors.scope}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          array={[{ id: '1', value: '1' }, { id: '2', value: '2' }, { id: '3', value: '3' }]}
          valueKey={'id'}
          labelKey={'value'}
        />
        {categories &&
          <FormSelectInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            allowEdit={true}
            disabled={isEditPage && editField !== 'categoryId'}
            title='Category'
            name='categoryId'
            value={formik.values.categoryId}
            touched={formik.touched.categoryId}
            errors={formik.errors.categoryId}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            array={categories.sort((a, b) => a.name.localeCompare(b.name))}
            valueKey={'id'}
            labelKey={'name'}
          />}
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'detailLevelOne'}
          title='Detail Level One'
          name='detailLevelOne'
          value={formik.values.detailLevelOne}
          touched={formik.touched.detailLevelOne}
          errors={formik.errors.detailLevelOne}
          cancelEditHandler={cancelEditHandler}
          onChange={formik.handleChange}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'detailLevelTwo'}
          title='Detail Level Two'
          name='detailLevelTwo'
          value={formik.values.detailLevelTwo}
          touched={formik.touched.detailLevelTwo}
          errors={formik.errors.detailLevelTwo}
          cancelEditHandler={cancelEditHandler}
          onChange={formik.handleChange}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'detailLevelThree'}
          title='Detail Level Three'
          name='detailLevelThree'
          value={formik.values.detailLevelThree}
          touched={formik.touched.detailLevelThree}
          errors={formik.errors.detailLevelThree}
          cancelEditHandler={cancelEditHandler}
          onChange={formik.handleChange}
        />
        {measurementUnits &&
          <FormSelectInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            allowEdit={true}
            disabled={isEditPage && editField !== 'measurementUnitId'}
            title='Measurement Unit'
            name='measurementUnitId'
            value={formik.values.measurementUnitId}
            touched={formik.touched.measurementUnitId}
            errors={formik.errors.measurementUnitId}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            array={measurementUnits.sort((a, b) => a.name.localeCompare(b.name))}
            valueKey={'id'}
            labelKey={'name'}
          />}
        {/* Selectable so factors can attribute to a specific gas — e.g. F-gas refrigerants
            (HFCs/PFCs/SF6/NF3) per-gas factors, not only combined "kg CO2e". */}
        {greenHouseGasUnits &&
          <FormSelectInputDisplay
            isEditPage={isEditPage}
            editField={editField}
            setEditField={setEditField}
            allowEdit={true}
            disabled={isEditPage && editField !== 'greenHouseGasUnitId'}
            title='Greenhouse Gas Unit'
            name='greenHouseGasUnitId'
            value={formik.values.greenHouseGasUnitId}
            touched={formik.touched.greenHouseGasUnitId}
            errors={formik.errors.greenHouseGasUnitId}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            array={greenHouseGasUnits.sort((a, b) => a.name.localeCompare(b.name))}
            valueKey={'id'}
            labelKey={'name'}
          />}
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'multiplier'}
          title='Multiplier'
          name='multiplier'
          type='number'
          value={formik.values.multiplier}
          touched={formik.touched.multiplier}
          errors={formik.errors.multiplier}
          cancelEditHandler={cancelEditHandler}
          onChange={formik.handleChange}
        />
        <FormCheckBoxInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'isRenewable'}
          title='Renewable Energy'
          name='isRenewable'
          label='Renewable Energy'
          isBottomRow={true}
          value={formik.values.isRenewable}
          touched={formik.touched.isRenewable}
          errors={formik.errors.isRenewable}
          cancelEditHandler={cancelEditHandler}
          onChange={formik.handleChange}
        />
        {!isEditPage &&
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={() => navigate(`/super-admin/conversion-factors/${conversionSourceId}`)}
              submitHandler={formik.submitForm}
            />
          </Stack>}
      </Box>
    )
  }